// pages/StandalonePage.jsx - Standalone Pesticide Application Form (No Jobber Job Required)
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import {
  Loader2, AlertCircle, Check, WifiOff, Home, User, MapPin
} from 'lucide-react'
import * as api from '../services/api'
import MethodSelector from '../components/MethodSelector'
import ProductSelector from '../components/ProductSelector'
import FormFields from '../components/FormFields'
import PhotoCapture from '../components/PhotoCapture'
import QuickAddProductModal from '../components/QuickAddProductModal'
import QuickAddEquipmentModal from '../components/QuickAddEquipmentModal'
import SubmissionSuccess from '../components/SubmissionSuccess'
import JobNumberBadge from '../components/JobNumberBadge'
import CompactWeatherDisplay from '../components/CompactWeatherDisplay'
import ProductAmountsSection from '../components/ProductAmountsSection'
import CustomerNameInput from '../components/CustomerNameInput'
import AddressInput from '../components/AddressInput'

// Single-page form - no steps needed

// Application methods (same as FormPage)
const APPLICATION_METHODS = [
  { id: 'foliar_spray', name: 'Foliar Spray', requiresCarrier: true },
  { id: 'soil_drench', name: 'Soil Drench', requiresCarrier: true },
  { id: 'trunk_bark_spray', name: 'Trunk/Bark Spray', requiresCarrier: true },
  { id: 'trunk_injection', name: 'Trunk Injection', requiresCarrier: false, allowsCarrier: true },
  { id: 'soil_injection', name: 'Soil Injection', requiresCarrier: true },
  { id: 'granular_broadcast', name: 'Granular/Broadcast', requiresCarrier: false }
]

export default function StandalonePage() {
  const { tenantId } = useParams()
  const [searchParams] = useSearchParams()
  
  // Loading & error states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  
  // Tenant and form data
  const [tenant, setTenant] = useState(null)
  const [products, setProducts] = useState([])
  const [applicators, setApplicators] = useState([])
  const [equipment, setEquipment] = useState([])
  
  // Weather
  const [weather, setWeather] = useState(null)
  const [weatherWarnings, setWeatherWarnings] = useState([])
  const [loadingWeather, setLoadingWeather] = useState(false)
  
  // Form state
  // Generate initial job number
  const generateInitialJobNumber = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let randomCode = ''
    for (let i = 0; i < 4; i++) {
      randomCode += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const year = String(now.getFullYear()).slice(-2)
    return `${randomCode} ${month}${day}${year}`
  }

  const [formData, setFormData] = useState({
    // Customer info (manually entered)
    customerName: searchParams.get('customer') || '',
    customerAddress: searchParams.get('address') || '',
    customerPhone: searchParams.get('phone') || '',
    jobNumber: searchParams.get('job') || generateInitialJobNumber(),

    // Application details
    applicationMethod: '',

    // Multi-product support
    selectedProducts: [], // Array of product names
    productAmounts: {},   // Object: { productName: { amount, unit } }

    // Application-wide carrier (applies to all products)
    carrierType: 'water',
    carrierAmount: '',
    carrierUnit: 'gal',

    // Shared application fields
    areaOrPlantTreated: '',
    equipment: '',
    applicatorName: '',
    applicatorLicense: '',
    supplier: '',

    // Weather (optional)
    temperature: '',
    humidity: '',
    windSpeed: '',
    windDirection: '',

    // Date/Time
    date: new Date().toLocaleDateString('en-US'),
    time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  })
  const [photos, setPhotos] = useState([])

  // UI state for inline editing
  const [weatherEditing, setWeatherEditing] = useState(false)
  const [jobNumberEditing, setJobNumberEditing] = useState(false)

  // Address options when a selected Jobber customer has multiple properties
  const [addressOptions, setAddressOptions] = useState([])

  // Modals
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [showAddEquipment, setShowAddEquipment] = useState(false)
  
  // Offline status
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])
  
  // Load tenant data
  useEffect(() => {
    loadTenantData()
  }, [tenantId])
  
  const loadTenantData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const data = await api.getStandaloneFormData(tenantId)
      
      setTenant(data.tenant)
      setProducts(data.products || [])
      setApplicators(data.applicators || [])
      setEquipment(data.equipment || [])
      
      // Set default applicator if only one
      if (data.applicators?.length === 1) {
        setFormData(prev => ({
          ...prev,
          applicatorName: data.applicators[0].name,
          applicatorLicense: data.applicators[0].licenseNumber
        }))
      }
      
    } catch (err) {
      console.error('Failed to load form:', err)
      setError(err.message || 'Failed to load form data')
    } finally {
      setLoading(false)
    }
  }
  
  // Load weather on mount using geolocation
  const loadWeather = useCallback(async () => {
    if (!tenant) return

    setLoadingWeather(true)

    try {
      // Try to get GPS coordinates first (prioritize geolocation)
      let lat, lon

      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 10000,
              enableHighAccuracy: false
            })
          })
          lat = position.coords.latitude
          lon = position.coords.longitude
        } catch (geoError) {
          console.log('Geolocation unavailable, weather can be entered manually')
        }
      }

      const result = await api.getStandaloneWeather(tenantId, lat, lon, formData.customerAddress)

      setWeather(result.weather)
      setWeatherWarnings(result.warnings || [])

      // Auto-fill weather fields
      if (result.weather && !result.weather.isDefault) {
        setFormData(prev => ({
          ...prev,
          temperature: result.weather.temperature?.toString() || prev.temperature,
          humidity: result.weather.humidity?.toString() || prev.humidity,
          windSpeed: result.weather.windSpeed?.toString() || prev.windSpeed,
          windDirection: result.weather.windDirection || prev.windDirection
        }))
      }

    } catch (err) {
      console.error('Failed to load weather:', err)
    } finally {
      setLoadingWeather(false)
    }
  }, [tenantId, tenant, formData.customerAddress])

  // Load weather on mount (using geolocation, not waiting for address)
  useEffect(() => {
    if (tenant && !weather) {
      loadWeather()
    }
  }, [tenant, weather, loadWeather])

  // Job number auto-generation
  const generateJobNumber = () => {
    // Generate random 4-character code (alphanumeric)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let randomCode = ''
    for (let i = 0; i < 4; i++) {
      randomCode += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    // Format date as MMDDYY
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const year = String(now.getFullYear()).slice(-2)
    const dateCode = `${month}${day}${year}`

    return `${randomCode} ${dateCode}`
  }

  // Auto-update job number when customer name changes (generates new random code)
  useEffect(() => {
    if (formData.customerName && !jobNumberEditing) {
      updateField('jobNumber', generateJobNumber())
    }
  }, [formData.customerName, jobNumberEditing])
  
  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Autofill from a selected Jobber customer
  const handleSelectCustomer = (customer) => {
    const addresses = customer.addresses || []

    setFormData(prev => ({
      ...prev,
      customerName: customer.name || prev.customerName,
      customerPhone: customer.phone || '',
      // Single address: fill it now. None or multiple: handled below.
      customerAddress: addresses.length === 1 ? addresses[0].formatted : ''
    }))

    // More than one property: let the applicator pick the service address
    setAddressOptions(addresses.length > 1 ? addresses : [])
  }

  // Choose a service address from the multi-address picker.
  // Keep the options visible so the selection stays highlighted and is changeable;
  // they reset when a new customer is selected or a new submission starts.
  const selectAddress = (formatted) => {
    updateField('customerAddress', formatted)
  }
  
  // Select application method
  const selectMethod = (methodId) => {
    updateField('applicationMethod', methodId)
    // Clear selected products when method changes
    updateField('selectedProducts', [])
    updateField('productAmounts', {})
  }

  // Add product to selection (multi-product)
  const addProduct = (productName) => {
    if (!formData.selectedProducts.includes(productName)) {
      updateField('selectedProducts', [...formData.selectedProducts, productName])

      // Find the product to get its default unit
      const product = products.find(p => p.name === productName)
      const defaultUnit = product?.defaultUnit || 'oz'

      // Initialize product amount entry (NO carrier - that's application-wide!)
      setFormData(prev => ({
        ...prev,
        productAmounts: {
          ...prev.productAmounts,
          [productName]: {
            amount: '',
            unit: defaultUnit
          }
        }
      }))
    }
  }

  // Remove product from selection
  const removeProduct = (productName) => {
    updateField('selectedProducts', formData.selectedProducts.filter(p => p !== productName))

    const newAmounts = { ...formData.productAmounts }
    delete newAmounts[productName]
    updateField('productAmounts', newAmounts)
  }

  // Update product amount
  const updateProductAmount = (productName, field, value) => {
    setFormData(prev => ({
      ...prev,
      productAmounts: {
        ...prev.productAmounts,
        [productName]: {
          ...prev.productAmounts[productName],
          [field]: value
        }
      }
    }))
  }
  
  // Select applicator
  const selectApplicator = (applicator) => {
    setFormData(prev => ({
      ...prev,
      applicatorName: applicator.name,
      applicatorLicense: applicator.licenseNumber
    }))
  }
  
  // Handle photo addition
  const handlePhotosChange = (newPhotos) => {
    setPhotos(newPhotos)
  }
  
  // Check if form is valid for submission
  const isFormValid = () => {
    // Check basic customer info
    if (!formData.customerName.trim() || !formData.customerAddress.trim()) {
      return false
    }

    // Check method selected
    if (!formData.applicationMethod) {
      return false
    }

    // Check at least one product selected
    if (formData.selectedProducts.length === 0) {
      return false
    }

    // Check all products have amounts
    for (const productName of formData.selectedProducts) {
      const productData = formData.productAmounts[productName]
      if (!productData || !productData.amount) {
        return false
      }
    }

    // Check carrier at APPLICATION level (not per-product!)
    const currentMethod = APPLICATION_METHODS.find(m => m.id === formData.applicationMethod)
    if (currentMethod?.requiresCarrier) {
      if (!formData.carrierAmount) {
        return false
      }
    }

    // Check shared application details
    if (!formData.areaOrPlantTreated || !formData.applicatorName || !formData.equipment) {
      return false
    }

    // Weather is optional - no validation needed

    return true
  }
  
  // Submit form
  const handleSubmit = async () => {
    if (submitting) return

    setSubmitting(true)

    try {
      // Build applications array (one per product)
      // Each product gets same carrier/application data, only product amount varies
      const applications = formData.selectedProducts.map(productName => {
        const productData = formData.productAmounts[productName]

        return {
          // Customer info
          customerName: formData.customerName,
          customerAddress: formData.customerAddress,
          customerPhone: formData.customerPhone,
          jobNumber: formData.jobNumber,

          // Product info (unique per product)
          productName: productName,
          productAmount: productData.amount,
          productUnit: productData.unit,

          // Carrier (SAME for all products in this application)
          carrierType: formData.carrierType || '',
          carrierAmount: formData.carrierAmount || '',
          carrierUnit: formData.carrierUnit || '',

          // Shared application details (SAME for all products)
          applicationMethod: formData.applicationMethod,
          areaOrPlantTreated: formData.areaOrPlantTreated,
          equipment: formData.equipment,
          applicatorName: formData.applicatorName,
          applicatorLicense: formData.applicatorLicense,
          supplier: formData.supplier,
          date: formData.date,
          time: formData.time,

          // Weather (optional, SAME for all products)
          temperature: formData.temperature || '',
          humidity: formData.humidity || '',
          windSpeed: formData.windSpeed || '',
          windDirection: formData.windDirection || ''
        }
      })

      if (!isOnline) {
        api.savePendingSubmission(`standalone_${tenantId}`, { applications, photos })
        setSubmitted(true)
        return
      }

      await api.submitStandaloneApplications(tenantId, applications, photos)
      setSubmitted(true)

    } catch (err) {
      console.error('Submission failed:', err)
      const applications = formData.selectedProducts.map(productName => {
        const productData = formData.productAmounts[productName]
        return {
          customerName: formData.customerName,
          customerAddress: formData.customerAddress,
          customerPhone: formData.customerPhone,
          jobNumber: formData.jobNumber,
          productName: productName,
          productAmount: productData.amount,
          productUnit: productData.unit,
          carrierType: formData.carrierType || '',
          carrierAmount: formData.carrierAmount || '',
          carrierUnit: formData.carrierUnit || '',
          applicationMethod: formData.applicationMethod,
          areaOrPlantTreated: formData.areaOrPlantTreated,
          equipment: formData.equipment,
          applicatorName: formData.applicatorName,
          applicatorLicense: formData.applicatorLicense,
          supplier: formData.supplier,
          date: formData.date,
          time: formData.time,
          temperature: formData.temperature || '',
          humidity: formData.humidity || '',
          windSpeed: formData.windSpeed || '',
          windDirection: formData.windDirection || ''
        }
      })
      api.savePendingSubmission(`standalone_${tenantId}`, { applications, photos })
      setError(`Submission failed: ${err.message}. Data saved locally for retry.`)
    } finally {
      setSubmitting(false)
    }
  }

  // Start new submission
  const startNewSubmission = () => {
    setSubmitted(false)
    setFormData(prev => ({
      ...prev,
      customerName: '',
      customerAddress: '',
      customerPhone: '',
      jobNumber: generateJobNumber(),
      applicationMethod: '',
      selectedProducts: [],
      productAmounts: {},
      carrierType: 'water',
      carrierAmount: '',
      areaOrPlantTreated: '',
      date: new Date().toLocaleDateString('en-US'),
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }))
    setPhotos([])
    setWeather(null)
    setAddressOptions([])
  }

  // Handle product added
  const handleProductAdded = (product) => {
    setProducts(prev => [...prev, product])
    addProduct(product.name)
    setShowAddProduct(false)
  }
  
  // Handle equipment added
  const handleEquipmentAdded = (equipmentItem) => {
    setEquipment(prev => [...prev, equipmentItem])
    updateField('equipment', equipmentItem.name)
    setShowAddEquipment(false)
  }
  
  // Get current method details
  const currentMethod = APPLICATION_METHODS.find(m => m.id === formData.applicationMethod)

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-arbor-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading form...</p>
        </div>
      </div>
    )
  }
  
  // Error state
  if (error && !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="card max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-xl font-semibold mt-4">Unable to Load Form</h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <div className="mt-6 space-y-3">
            <button 
              onClick={loadTenantData}
              className="btn-primary w-full"
            >
              Try Again
            </button>
            <Link to="/admin" className="btn-secondary w-full flex items-center justify-center gap-2">
              <Home className="w-4 h-4" />
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }
  
  // Success state
  if (submitted) {
    return (
      <SubmissionSuccess 
        formLink={{
          jobNumber: formData.jobNumber,
          clientName: formData.customerName,
          companyName: tenant?.companyName
        }}
        onNewSubmission={startNewSubmission}
        isOffline={!isOnline}
      />
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-arbor-500 text-white sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-semibold">{tenant?.companyName || 'Pesticide Record'}</h1>
              <p className="text-sm text-arbor-100">
                Standalone Application Record
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!isOnline && (
                <div className="flex items-center gap-1 text-yellow-200">
                  <WifiOff className="w-4 h-4" />
                  <span className="text-xs">Offline</span>
                </div>
              )}
              <Link to="/admin" className="p-2 text-arbor-200 hover:text-white">
                <Home className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      
      {/* Single-page form */}
      <main className="pb-24">
        <div className="p-4 space-y-6">
          {/* 1. Customer Information with inline Job Number badge */}
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-arbor-500" />
                Customer Information
              </h3>
              <JobNumberBadge
                value={formData.jobNumber}
                onChange={(value) => updateField('jobNumber', value)}
                customerName={formData.customerName}
                editing={jobNumberEditing}
                onToggleEdit={() => setJobNumberEditing(!jobNumberEditing)}
              />
            </div>

            <div>
              <label className="form-label">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <CustomerNameInput
                value={formData.customerName}
                onChange={(value) => updateField('customerName', value)}
                onSelectCustomer={handleSelectCustomer}
                tenantId={tenantId}
              />
            </div>

            <div>
              <label className="form-label">
                Service Address <span className="text-red-500">*</span>
              </label>

              {/* Multiple Jobber properties on file: an optional shortcut to prefill
                  the service address. Kept subtle so the input stays the primary field. */}
              {addressOptions.length > 1 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                    Prefill from an address on file
                  </p>
                  <div className="space-y-2">
                    {addressOptions.map((addr) => {
                      const isSelected = formData.customerAddress === addr.formatted
                      return (
                        <button
                          key={addr.id}
                          type="button"
                          onClick={() => selectAddress(addr.formatted)}
                          className={`w-full flex items-center gap-3 text-left px-4 py-2.5 rounded-lg border transition-colors ${
                            isSelected
                              ? 'border-arbor-400 bg-arbor-50'
                              : 'border-gray-200 bg-white hover:border-arbor-300 hover:bg-gray-50'
                          }`}
                        >
                          <MapPin
                            className={`w-4 h-4 flex-shrink-0 ${
                              isSelected ? 'text-arbor-500' : 'text-gray-400'
                            }`}
                          />
                          <span
                            className={`flex-1 text-sm leading-snug ${
                              isSelected ? 'text-arbor-800 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {addr.formatted}
                          </span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-arbor-500 flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              <AddressInput
                value={formData.customerAddress}
                onChange={(value) => updateField('customerAddress', value)}
                tenantId={tenantId}
                placeholder="e.g., 123 Main St, Portland, OR 97201"
              />
              <p className="text-xs text-gray-500 mt-1">
                Full address including city and zip for compliance records
              </p>
            </div>

            <div>
              <label className="form-label">
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="e.g., (503) 555-1234"
                value={formData.customerPhone}
                onChange={(e) => updateField('customerPhone', e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          {/* 2. Weather Conditions - Compact display with edit button */}
          <CompactWeatherDisplay
            weather={weather}
            warnings={weatherWarnings}
            formData={formData}
            updateField={updateField}
            loading={loadingWeather}
            onRefresh={loadWeather}
            editing={weatherEditing}
            onToggleEdit={() => setWeatherEditing(!weatherEditing)}
          />

          {/* 3. Application Method */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3">
              Application Method <span className="text-red-500">*</span>
            </h3>
            <MethodSelector
              methods={APPLICATION_METHODS}
              selectedMethod={formData.applicationMethod}
              onSelect={selectMethod}
            />
          </div>

          {/* 4. Product Selection - Multi-select, show if method is selected */}
          {formData.applicationMethod && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-3">
                Select Products <span className="text-red-500">*</span>
              </h3>
              <ProductSelector
                products={products}
                selectedProducts={formData.selectedProducts}
                method={formData.applicationMethod}
                onSelect={addProduct}
                onRemove={removeProduct}
                onAddNew={() => setShowAddProduct(true)}
                shortCode={`standalone_${tenantId}`}
                multiSelect={true}
              />
            </div>
          )}

          {/* 5. Application Details - Show if products are selected */}
          {formData.selectedProducts.length > 0 && (
            <div className="card space-y-6">
              <h3 className="font-semibold text-gray-900">
                Application Details <span className="text-red-500">*</span>
              </h3>

              {/* Product Amounts Section - each product gets amount + unit only */}
              <ProductAmountsSection
                selectedProducts={formData.selectedProducts}
                productAmounts={formData.productAmounts}
                updateProductAmount={updateProductAmount}
                removeProduct={removeProduct}
              />

              {/* Shared application fields - including application-wide carrier */}
              <FormFields
                formData={formData}
                updateField={updateField}
                applicators={applicators}
                equipment={equipment}
                selectedApplicator={formData.applicatorName}
                onSelectApplicator={selectApplicator}
                requiresCarrier={currentMethod?.requiresCarrier}
                showCarrier={currentMethod?.requiresCarrier || currentMethod?.allowsCarrier}
                onAddEquipment={() => setShowAddEquipment(true)}
                hideProductFields={true}
              />
            </div>
          )}

          {/* 6. Photos - Optional, show if products are selected */}
          {formData.selectedProducts.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-3">
                Photos (Optional)
              </h3>
              <PhotoCapture
                photos={photos}
                onChange={handlePhotosChange}
              />
            </div>
          )}
        </div>
      </main>
      
      {/* Bottom submit button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 safe-area-inset-bottom">
        <button
          onClick={handleSubmit}
          disabled={submitting || !isFormValid()}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Submit Application Record
            </>
          )}
        </button>
      </div>
      
      {/* Modals */}
      {showAddProduct && (
        <QuickAddProductModal
          shortCode={`standalone_${tenantId}`}
          onAdd={handleProductAdded}
          onClose={() => setShowAddProduct(false)}
        />
      )}
      
      {showAddEquipment && (
        <QuickAddEquipmentModal
          shortCode={`standalone_${tenantId}`}
          onAdd={handleEquipmentAdded}
          onClose={() => setShowAddEquipment(false)}
        />
      )}
    </div>
  )
}
